import React, { useEffect, useState } from "react";
import { getFirestore, doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import app from "../../firebase";
import { formatCalendarDate, getCalendarRangeStatus, useCalendarClock } from "../../utils/announcementDates";

function getStudentFees(fees, studentUnits) {
  return fees.map(fee => {
    if (fee.perUnit !== undefined) {
      // Per-unit fee: use student's units
      return {
        ...fee,
        calculatedAmount: studentUnits * fee.perUnit,
        formula: `(${studentUnits} units X ${fee.perUnit})`
      };
    } else {
      // Fixed fee
      return {
        ...fee,
        calculatedAmount: fee.amount,
        formula: fee.formula || ""
      };
    }
  });
}

export default function MiscellaneousFees() {
  const todayKey = useCalendarClock();
  const [fees, setFees] = useState([]);
  const [cashDiscount, setCashDiscount] = useState(1000);
  const [secondSemCashDiscount, setSecondSemCashDiscount] = useState(0);
  const [firstSemCashWithDiscountDate, setFirstSemCashWithDiscountDate] = useState("");
  const [firstSemCashWithoutDiscountDate, setFirstSemCashWithoutDiscountDate] = useState("");
  const [secondSemCashWithDiscountDate, setSecondSemCashWithDiscountDate] = useState("");
  const [secondSemCashWithoutDiscountDate, setSecondSemCashWithoutDiscountDate] = useState("");
  const [firstSemInstallmentDates, setFirstSemInstallmentDates] = useState(["", "", "", "", ""]);
  const [secondSemInstallmentDates, setSecondSemInstallmentDates] = useState(["", "", "", "", ""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Read-only semester info from announcement
  const [firstSemYearStart, setFirstSemYearStart] = useState("");
  const [firstSemYearEnd, setFirstSemYearEnd] = useState("");
  const [firstSemStart, setFirstSemStart] = useState("");
  const [firstSemEnd, setFirstSemEnd] = useState("");
  const [secondSemYearStart, setSecondSemYearStart] = useState("");
  const [secondSemYearEnd, setSecondSemYearEnd] = useState("");
  const [secondSemStart, setSecondSemStart] = useState("");
  const [secondSemEnd, setSecondSemEnd] = useState("");

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "—";
    return formatCalendarDate(dateStr) || "—";
  };

  const getSemesterStatus = (startDate, endDate) => {
    return getCalendarRangeStatus(startDate, endDate, todayKey);
  };

  const getStatusStyle = (status) => {
    if (status === "Ongoing") return { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" };
    if (status === "Ended") return { background: "#ffebee", color: "#c62828", border: "1px solid #ef9a9a" };
    return { background: "#fff8e1", color: "#b26a00", border: "1px solid #ffe0b2" };
  };

  useEffect(() => {
    const db = getFirestore(app);
    const ref = doc(db, "systemSettings", "miscellaneousFees");
    const unsubscribe = onSnapshot(ref, (snap) => {
      setLoading(true);
      if (snap.exists()) {
        const data = snap.data();
        setFees(data.fees || []);
        setCashDiscount(data.cashDiscount || 1000);
        setSecondSemCashDiscount(data.secondSemCashDiscount || 0);
        setFirstSemCashWithDiscountDate(data.firstSemCashWithDiscountDate || data.cashWithDiscountDate || "");
        setFirstSemCashWithoutDiscountDate(data.firstSemCashWithoutDiscountDate || data.cashWithoutDiscountDate || "");
        setSecondSemCashWithDiscountDate(data.secondSemCashWithDiscountDate || "");
        setSecondSemCashWithoutDiscountDate(data.secondSemCashWithoutDiscountDate || "");
        setFirstSemInstallmentDates(data.firstSemInstallmentDates || data.installmentDates || ["", "", "", "", ""]);
        setSecondSemInstallmentDates(data.secondSemInstallmentDates || ["", "", "", "", ""]);
      }
      setLoading(false);
    }, (err) => {
      console.error("Error fetching fees:", err);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Fetch announcement data for semester context
  useEffect(() => {
    const db = getFirestore(app);
    const ref = doc(db, "systemSettings", "announcement");
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setFirstSemYearStart(data.firstSemYearStart || "");
        setFirstSemYearEnd(data.firstSemYearEnd || "");
        setFirstSemStart(data.firstSemStart || "");
        setFirstSemEnd(data.firstSemEnd || "");
        setSecondSemYearStart(data.secondSemYearStart || "");
        setSecondSemYearEnd(data.secondSemYearEnd || "");
        setSecondSemStart(data.secondSemStart || "");
        setSecondSemEnd(data.secondSemEnd || "");
      }
    }, (err) => {
      console.error("Error fetching announcement:", err);
    });
    return unsubscribe;
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const db = getFirestore(app);
      await setDoc(doc(db, "systemSettings", "miscellaneousFees"), {
        fees,
        cashDiscount,
        secondSemCashDiscount,
        firstSemCashWithDiscountDate,
        firstSemCashWithoutDiscountDate,
        secondSemCashWithDiscountDate,
        secondSemCashWithoutDiscountDate,
        firstSemInstallmentDates,
        secondSemInstallmentDates,
        cashWithDiscountDate: firstSemCashWithDiscountDate,
        cashWithoutDiscountDate: firstSemCashWithoutDiscountDate,
        installmentDates: firstSemInstallmentDates,
      }, { merge: true });
      setMessage("✅ Miscellaneous fees updated successfully.");
    } catch {
      setMessage("❌ Failed to update fees.");
    }
    setSaving(false);
  };

  const handleFeeChange = (idx, key, value) => {
    setFees(fees => fees.map((fee, i) => i === idx ? { ...fee, [key]: value } : fee));
  };
  
  const handleAddFixedFee = () => {
    setFees([...fees, { label: "", amount: 0, type: "fixed" }]);
  };
  
  const handleAddPerUnitFee = () => {
    setFees([...fees, { label: "", perUnit: 0, type: "perUnit" }]);
  };
  
  const handleRemoveFee = (idx) => setFees(fees => fees.filter((_, i) => i !== idx));

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Loading...</div>;

  const studentUnits = 10; // Replace with actual student units
  const studentFees = getStudentFees(fees, studentUnits);
  const totalFees = studentFees.reduce((sum, fee) => sum + fee.calculatedAmount, 0);

  return (
    <div style={{ background: '#f9fafb', minHeight: '100vh', padding: '32px 16px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ color: '#155c2b', fontWeight: 700, fontSize: 28, margin: 0 }}>Miscellaneous Fees Management</h1>
          <p style={{ color: '#666', marginTop: 8 }}>Configure all miscellaneous fees, discounts, and payment schedules</p>
        </div>

        <form onSubmit={handleSave}>
          
          {/* SECTION 1: Fees Configuration */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h2 style={{ color: '#155c2b', fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 24, borderBottom: '2px solid #e0e0e0', paddingBottom: 16 }}>Fees</h2>
            
            {/* Fees Table */}
            <div style={{ overflowX: 'auto', marginBottom: 24 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ textAlign: "left", padding: '12px 16px', fontWeight: 600, color: '#333', borderBottom: '2px solid #ddd' }}>Fee Name</th>
                    <th style={{ textAlign: "left", padding: '12px 16px', fontWeight: 600, color: '#333', borderBottom: '2px solid #ddd' }}>Type</th>
                    <th style={{ textAlign: "left", padding: '12px 16px', fontWeight: 600, color: '#333', borderBottom: '2px solid #ddd' }}>Amount / Formula</th>
                    <th style={{ textAlign: "center", padding: '12px 16px', fontWeight: 600, color: '#333', borderBottom: '2px solid #ddd' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {fees.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#999' }}>No fees added yet. Click "Add Fixed Fee" or "Add Per-Unit Fee" below.</td>
                    </tr>
                  ) : (
                    fees.map((fee, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee', background: idx % 2 === 0 ? '#fafafa' : '#fff' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <input
                            type="text"
                            placeholder="e.g., Library Fee, Lab Fee"
                            value={fee.label}
                            onChange={e => handleFeeChange(idx, "label", e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }}
                          />
                        </td>
                        <td style={{ padding: '12px 16px', color: '#666', fontSize: 14, fontWeight: 500 }}>
                          {fee.perUnit !== undefined ? '📊 Per Unit' : '💰 Fixed'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {fee.perUnit !== undefined ? (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <input
                                type="number"
                                placeholder="Per Unit Amount"
                                value={fee.perUnit || ""}
                                onChange={e => handleFeeChange(idx, "perUnit", parseFloat(e.target.value) || 0)}
                                style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }}
                              />
                              <span style={{ fontSize: 12, color: '#999' }}>per unit</span>
                            </div>
                          ) : (
                            <input
                              type="number"
                              placeholder="Fixed Amount"
                              value={fee.amount || ""}
                              onChange={e => handleFeeChange(idx, "amount", parseFloat(e.target.value) || 0)}
                              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }}
                            />
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <button 
                            type="button" 
                            onClick={() => handleRemoveFee(idx)} 
                            style={{ background: '#ffebee', color: '#c62828', border: 'none', borderRadius: 6, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Add Fee Buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button 
                type="button" 
                onClick={handleAddFixedFee} 
                style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #81c784', borderRadius: 6, padding: '10px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
              >
                + Add Fixed Fee
              </button>
              <button 
                type="button" 
                onClick={handleAddPerUnitFee} 
                style={{ background: '#e3f2fd', color: '#1565c0', border: '1px solid #64b5f6', borderRadius: 6, padding: '10px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
              >
                + Add Per-Unit Fee
              </button>
            </div>
          </div>

          {/* SECTION 2: Payment Discounts */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h2 style={{ color: '#155c2b', fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 24, borderBottom: '2px solid #e0e0e0', paddingBottom: 16 }}>Payment Discounts</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
                <div style={{ fontWeight: 700, color: '#155c2b', marginBottom: 12 }}>First Semester</div>
                
                {/* Read-only semester context */}
                <div style={{ ...getStatusStyle(getSemesterStatus(firstSemStart, firstSemEnd)), borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>📚 Semester Context</div>
                    <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, fontWeight: 700, background: 'rgba(0,0,0,0.1)' }}>
                      {getSemesterStatus(firstSemStart, firstSemEnd)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <strong>School Year:</strong> {firstSemYearStart && firstSemYearEnd 
                      ? `${firstSemYearStart}-${firstSemYearEnd}`
                      : "Not set"}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <strong>Semester Period:</strong> {formatDisplayDate(firstSemStart)} to {formatDisplayDate(firstSemEnd)}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                  <div>
                    <label style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>💵 Cash Discount Amount</label>
                    <p style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>Discount given for full cash payment</p>
                    <input 
                      type="number" 
                      value={cashDiscount} 
                      onChange={e => setCashDiscount(parseFloat(e.target.value) || 0)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <label style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>📅 Cash Payment WITH Discount Deadline</label>
                      <p style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>Last day to pay with discount</p>
                      <input 
                        type="date" 
                        value={firstSemCashWithDiscountDate} 
                        onChange={e => setFirstSemCashWithDiscountDate(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>📅 Cash Payment WITHOUT Discount Deadline</label>
                      <p style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>Last day to pay without discount</p>
                      <input 
                        type="date" 
                        value={firstSemCashWithoutDiscountDate} 
                        onChange={e => setFirstSemCashWithoutDiscountDate(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
                <div style={{ fontWeight: 700, color: '#155c2b', marginBottom: 12 }}>Second Semester</div>
                
                {/* Read-only semester context */}
                <div style={{ ...getStatusStyle(getSemesterStatus(secondSemStart, secondSemEnd)), borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>📚 Semester Context</div>
                    <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, fontWeight: 700, background: 'rgba(0,0,0,0.1)' }}>
                      {getSemesterStatus(secondSemStart, secondSemEnd)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <strong>School Year:</strong> {secondSemYearStart && secondSemYearEnd 
                      ? `${secondSemYearStart}-${secondSemYearEnd}`
                      : "Not set"}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <strong>Semester Period:</strong> {formatDisplayDate(secondSemStart)} to {formatDisplayDate(secondSemEnd)}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                  <div>
                    <label style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>💵 Cash Discount Amount</label>
                    <p style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>Discount given for full cash payment</p>
                    <input 
                      type="number" 
                      value={secondSemCashDiscount} 
                      onChange={e => setSecondSemCashDiscount(parseFloat(e.target.value) || 0)} 
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <label style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>📅 Cash Payment WITH Discount Deadline</label>
                      <p style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>Last day to pay with discount</p>
                      <input 
                        type="date" 
                        value={secondSemCashWithDiscountDate} 
                        onChange={e => setSecondSemCashWithDiscountDate(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </div>

                    <div>
                      <label style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>📅 Cash Payment WITHOUT Discount Deadline</label>
                      <p style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>Last day to pay without discount</p>
                      <input 
                        type="date" 
                        value={secondSemCashWithoutDiscountDate} 
                        onChange={e => setSecondSemCashWithoutDiscountDate(e.target.value)}
                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 3: Installment Payment Schedule */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <h2 style={{ color: '#155c2b', fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 24, borderBottom: '2px solid #e0e0e0', paddingBottom: 16 }}>Installment Payment Schedule</h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
                <div style={{ fontWeight: 700, color: '#155c2b', marginBottom: 12 }}>First Semester</div>
                
                {/* Read-only semester context */}
                <div style={{ ...getStatusStyle(getSemesterStatus(firstSemStart, firstSemEnd)), borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>📚 Semester Context</div>
                    <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, fontWeight: 700, background: 'rgba(0,0,0,0.1)' }}>
                      {getSemesterStatus(firstSemStart, firstSemEnd)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <strong>School Year:</strong> {firstSemYearStart && firstSemYearEnd 
                      ? `${firstSemYearStart}-${firstSemYearEnd}`
                      : "Not set"}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <strong>Semester Period:</strong> {formatDisplayDate(firstSemStart)} to {formatDisplayDate(firstSemEnd)}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {firstSemInstallmentDates.map((date, idx) => {
                    const labels = ['🔴 Downpayment', '🟡 1st Installment', '🟠 2nd Installment', '🔵 3rd Installment', '🟢 4th Installment'];
                    return (
                      <div key={idx}>
                        <label style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>
                          {labels[idx]}
                        </label>
                        <input
                          type="date"
                          value={date}
                          onChange={e => setFirstSemInstallmentDates(dates => dates.map((d, i) => i === idx ? e.target.value : d))}
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
                <div style={{ fontWeight: 700, color: '#155c2b', marginBottom: 12 }}>Second Semester</div>
                
                {/* Read-only semester context */}
                <div style={{ ...getStatusStyle(getSemesterStatus(secondSemStart, secondSemEnd)), borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>📚 Semester Context</div>
                    <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 999, fontWeight: 700, background: 'rgba(0,0,0,0.1)' }}>
                      {getSemesterStatus(secondSemStart, secondSemEnd)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <strong>School Year:</strong> {secondSemYearStart && secondSemYearEnd 
                      ? `${secondSemYearStart}-${secondSemYearEnd}`
                      : "Not set"}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <strong>Semester Period:</strong> {formatDisplayDate(secondSemStart)} to {formatDisplayDate(secondSemEnd)}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  {secondSemInstallmentDates.map((date, idx) => {
                    const labels = ['🔴 Downpayment', '🟡 1st Installment', '🟠 2nd Installment', '🔵 3rd Installment', '🟢 4th Installment'];
                    return (
                      <div key={idx}>
                        <label style={{ fontWeight: 600, color: '#333', display: 'block', marginBottom: 8 }}>
                          {labels[idx]}
                        </label>
                        <input
                          type="date"
                          value={date}
                          onChange={e => setSecondSemInstallmentDates(dates => dates.map((d, i) => i === idx ? e.target.value : d))}
                          style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 24 }}>
            <button 
              type="submit" 
              disabled={saving} 
              style={{ background: '#155c2b', color: '#fff', border: 'none', borderRadius: 6, padding: '12px 32px', fontWeight: 700, fontSize: 16, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
            >
              {saving ? '💾 Saving...' : '💾 Save All Changes'}
            </button>
          </div>

          {/* Message */}
          {message && (
            <div style={{ 
              background: message.includes('✅') ? '#e8f5e9' : '#ffebee',
              color: message.includes('✅') ? '#2e7d32' : '#c62828',
              border: message.includes('✅') ? '1px solid #81c784' : '1px solid #ef5350',
              borderRadius: 6, 
              padding: 16, 
              marginBottom: 24,
              fontWeight: 600,
              textAlign: 'center'
            }}>
              {message}
            </div>
          )}

        </form>

        {/* SECTION 4: Student Fee Preview */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <h2 style={{ color: '#155c2b', fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 24, borderBottom: '2px solid #e0e0e0', paddingBottom: 16 }}>Student Fee Summary (Preview)</h2>
          
          <p style={{ fontSize: 14, color: '#666', marginBottom: 18 }}>
            💡 <strong>Sample Preview:</strong> Student with {studentUnits} units
          </p>

          {studentFees.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#999', background: '#f9f9f9', borderRadius: 6 }}>
              No fees configured yet.
            </div>
          ) : (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
                <tbody>
                  {studentFees.map((fee, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={{ padding: '12px 0', fontSize: 14, color: '#333' }}>
                        <strong>{fee.label}</strong>
                        {fee.formula && <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>({fee.formula})</span>}
                      </td>
                      <td style={{ padding: '12px 0', textAlign: "right", fontSize: 14, fontWeight: 600, color: '#155c2b' }}>
                        ₱ {fee.calculatedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total */}
              <div style={{ 
                borderTop: '2px solid #155c2b', 
                paddingTop: 16, 
                display: 'flex', 
                justifyContent: 'space-between',
                fontSize: 18,
                fontWeight: 700,
                color: '#155c2b'
              }}>
                <span>Total Miscellaneous Fees:</span>
                <span>₱ {totalFees.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}